/**
 * Reads and writes UnifiedAppData as small records: one per state section and
 * one per game. A save writes only the records that changed, so adding a game
 * writes that game plus the few small sections it touched. Saves run one at
 * a time.
 */
import { type GameHistoryEntry, migrateHistory } from "../../utils/gameHistory";
import {
  createDefaultData,
  migrateData,
  type UnifiedAppData,
} from "../unifiedDataSchema";
import { backupCorrupted, writeLegacyBlob } from "./legacy";
import { migrateLegacy, readLegacy } from "./migrateLegacy";
import { isQuotaError } from "./quota";
import {
  isPlainObject,
  MIGRATED_MARKER,
  sectionJson,
  STATE_SECTIONS,
  type StateSection,
} from "./sections";
import { CorruptRecord, type HistoryKey, type StorageAdapter } from "./types";

/** The newest stored games are never deleted to make room. */
export const KEEP_NEWEST_GAMES = 20;
/** At most this many stored games are deleted for room in one session. */
export const MAX_EVICTIONS_PER_SESSION = 10;

export interface LoadResult {
  data: UnifiedAppData;
  /** False when stored data could not be read: never write over it. */
  writable: boolean;
}

interface Plan {
  puts: [HistoryKey, GameHistoryEntry][];
  dels: HistoryKey[];
  state: [StateSection, string][];
}

export class Persistence {
  // Games known to be stored, both ways.
  private keyOf = new Map<GameHistoryEntry, HistoryKey>();
  private entryAt = new Map<HistoryKey, GameHistoryEntry>();
  // Games removed from storage to fit the quota; kept in memory only.
  private evicted = new WeakSet<GameHistoryEntry>();
  private nextKey = 1;
  // Last stored JSON of each state section.
  private written = new Map<StateSection, string>();
  private queue: Promise<unknown> = Promise.resolve();
  // Games deleted for room this session, and whether deleting may continue.
  private evictions = 0;
  private mayEvict = true;
  // This session saves to the legacy blob: the record store is unusable.
  private legacy = false;

  constructor(private readonly adapter: StorageAdapter) {}

  /**
   * Load from the records, migrating the legacy blob on first run. If the
   * record store can't be used (or the move into it failed), the session
   * runs on the legacy blob and saves there, so a later migration picks up
   * its games. Once the migration marker is stored, never.
   */
  async load(): Promise<LoadResult> {
    let state: Map<string, unknown>;
    try {
      state = await this.adapter.readState();
    } catch (error) {
      console.warn("⚠️ Could not open the record store:", error);
      const data = await readLegacy();
      if (!data) return { data: createDefaultData(), writable: false };
      return this.useLegacy(data);
    }
    if (state.has(MIGRATED_MARKER)) return this.readRecords(state);

    const migration = await migrateLegacy(this.adapter);
    if (!migration.ok) {
      if (!migration.legacyWritable || (await this.markerStored())) {
        return { data: migration.data, writable: false };
      }
      return this.useLegacy(migration.data);
    }
    for (const [key, entry] of migration.history) this.track(key, entry);
    this.nextKey = migration.history.length + 1;
    this.written = new Map(migration.sections);
    return { data: migration.data, writable: true };
  }

  private useLegacy(data: UnifiedAppData): LoadResult {
    console.warn(
      "⚠️ Record store unusable; this session saves to the legacy blob",
    );
    this.legacy = true;
    return { data, writable: true };
  }

  /** Whether the marker is stored after all (unknown counts as no). */
  private async markerStored(): Promise<boolean> {
    try {
      return (await this.adapter.readState()).has(MIGRATED_MARKER);
    } catch {
      return false;
    }
  }

  /**
   * Assemble data from the records. A bad record never costs the others: a
   * bad game is skipped and left on disk; a bad section loads as its default
   * and may be overwritten only after a backup of it is safe.
   */
  private async readRecords(state: Map<string, unknown>): Promise<LoadResult> {
    const data = createDefaultData();
    const sections = data as unknown as Record<StateSection, unknown>;
    let writable = true;
    for (const s of STATE_SECTIONS) {
      const raw = state.get(s);
      if (raw === undefined) continue;
      if (isPlainObject(raw)) {
        sections[s] = raw;
        this.written.set(s, JSON.stringify(raw));
        continue;
      }
      console.error(`🚨 Stored ${s} is unreadable; using defaults`);
      const value = raw instanceof CorruptRecord ? raw.raw : raw;
      if (!(await backupCorrupted(value, s))) writable = false;
    }

    let records: [HistoryKey, unknown][];
    try {
      records = await this.adapter.readHistory();
    } catch (error) {
      console.error("🚨 Could not read game history:", error);
      return { data: migrateData(data), writable: false };
    }
    data.gameHistory = this.adoptHistory(records);
    return { data: migrateData(data), writable };
  }

  private adoptHistory(records: [HistoryKey, unknown][]): GameHistoryEntry[] {
    const history: GameHistoryEntry[] = [];
    for (const [key, raw] of records) {
      this.nextKey = Math.max(this.nextKey, key + 1);
      const entry =
        raw instanceof CorruptRecord ? undefined : migrateHistory([raw])[0];
      if (!entry) {
        console.error(`🚨 Skipped unreadable game record ${key}`);
        continue;
      }
      this.track(key, entry);
      history.push(entry);
    }
    return history.reverse(); // newest first
  }

  /**
   * Persist what changed since the last save. Saves are queued, so
   * overlapping calls never interleave their writes. Resolves true when all
   * of `data` is stored, false when storage is full and some of it could
   * not be; rejects on other storage errors.
   */
  save(data: UnifiedAppData): Promise<boolean> {
    const run = this.queue.then(() => this.write(data));
    this.queue = run.catch(() => undefined);
    return run;
  }

  /**
   * On a quota error nothing stored is touched and memory keeps everything;
   * the unsaved changes stay pending for a later save. Only a save that adds
   * games may make room, by deleting the oldest stored games one at a time
   * and retrying after each. That is bounded: the newest KEEP_NEWEST_GAMES
   * are never deleted, a session deletes at most MAX_EVICTIONS_PER_SESSION,
   * and once deleting fails to make room (space not yet reclaimed, or used
   * by other data) the session stops deleting.
   */
  private async write(data: UnifiedAppData): Promise<boolean> {
    if (this.legacy) return this.writeLegacy(data);
    const plan = this.plan(data);
    try {
      await this.apply(plan);
      return true;
    } catch (error) {
      if (!isQuotaError(error)) throw error;
    }
    if (plan.puts.length === 0 || !this.mayEvict) {
      console.error("🚨 Storage quota exceeded; changes kept in memory");
      return false;
    }
    console.error(
      "🚨 Storage quota exceeded; removing the oldest stored games to make room",
    );
    let removed = 0;
    for (;;) {
      const oldest = this.evictable();
      if (oldest === undefined) {
        this.mayEvict = false;
        console.error("🚨 Could not save within the storage quota");
        return false;
      }
      await this.adapter.deleteHistory([oldest]);
      this.evict(oldest);
      this.evictions++;
      removed++;
      try {
        await this.apply(this.plan(data));
        console.error(`🚨 Saved after removing the ${removed} oldest games`);
        return false;
      } catch (error) {
        if (!isQuotaError(error)) throw error;
      }
    }
  }

  /** Legacy-blob saves: a full store keeps the changes pending. */
  private async writeLegacy(data: UnifiedAppData): Promise<boolean> {
    try {
      await writeLegacyBlob(data);
      return true;
    } catch (error) {
      if (!isQuotaError(error)) throw error;
      console.error("🚨 Storage quota exceeded; changes kept in memory");
      return false;
    }
  }

  /** The oldest stored game that may be deleted for room, if any. */
  private evictable(): HistoryKey | undefined {
    if (this.evictions >= MAX_EVICTIONS_PER_SESSION) return undefined;
    if (this.entryAt.size <= KEEP_NEWEST_GAMES) return undefined;
    let oldest: HistoryKey | undefined;
    for (const key of this.entryAt.keys()) {
      if (oldest === undefined || key < oldest) oldest = key;
    }
    return oldest;
  }

  private plan(data: UnifiedAppData): Plan {
    const present = new Set<HistoryKey>();
    const fresh: GameHistoryEntry[] = [];
    for (const entry of data.gameHistory) {
      const key = this.keyOf.get(entry);
      if (key !== undefined) present.add(key);
      else if (!this.evicted.has(entry)) fresh.push(entry);
    }
    // Keys follow the newest-first order: the oldest new game gets the lowest.
    let next = this.nextKey;
    const puts = fresh
      .reverse()
      .map((e): [HistoryKey, GameHistoryEntry] => [next++, e]);
    const dels = [...this.entryAt.keys()].filter((k) => !present.has(k));
    const state = sectionJson(data).filter(
      ([s, json]) => this.written.get(s) !== json,
    );
    return { puts, dels, state };
  }

  /** New games first, then the sections that count them, then removals. */
  private async apply({ puts, dels, state }: Plan): Promise<void> {
    if (puts.length > 0) await this.adapter.writeHistory(puts);
    if (state.length > 0) {
      await this.adapter.writeState(state.map(([s, j]) => [s, JSON.parse(j)]));
    }
    if (dels.length > 0) await this.adapter.deleteHistory(dels);

    for (const [key, entry] of puts) this.track(key, entry);
    if (puts.length > 0) this.nextKey = puts[puts.length - 1][0] + 1;
    for (const [s, json] of state) this.written.set(s, json);
    for (const key of dels) this.untrack(key);
  }

  private track(key: HistoryKey, entry: GameHistoryEntry): void {
    this.keyOf.set(entry, key);
    this.entryAt.set(key, entry);
  }

  private untrack(key: HistoryKey): GameHistoryEntry | undefined {
    const entry = this.entryAt.get(key);
    this.entryAt.delete(key);
    if (entry) this.keyOf.delete(entry);
    return entry;
  }

  private evict(key: HistoryKey): void {
    const entry = this.untrack(key);
    if (entry) this.evicted.add(entry);
  }
}
